<template>
  <div class="card height-equal position-relative">
    <jurisdiction-check-modal
        v-if="!isJurisdictionValidated"
        :api-key="googleMapsApiKey"
        @validated="onJurisdictionValidated"
    />

    <div v-if="isSaving" class="saving-overlay">
      <div class="spinner-border text-primary" role="status" aria-live="polite">
        <span class="visually-hidden">Processing...</span>
      </div>
    </div>

    <div class="card-header ar-signup-header d-flex justify-content-between align-items-start flex-wrap gap-2"
         v-show="isJurisdictionValidated && !submittedState">
      <div>
        <h2 class="mb-1">Become an Independent Driver</h2>
        <p class="mb-0 text-muted">Complete the steps below to register as an independent driver.</p>
      </div>
      <div class="text-nowrap">
        <span class="text-muted me-1">Already have an account?</span>
        <a href="/driver/login" class="ar-signup-link fw-semibold">Log in</a>
      </div>
    </div>

    <!-- Post-submission application status view -->
    <div v-if="submittedState && isJurisdictionValidated" class="card-body py-5">
      <div class="text-center mb-4">
        <div class="mb-3">
          <span class="d-inline-flex align-items-center justify-content-center rounded-circle"
                style="width:80px;height:80px;background:rgba(255,193,7,0.15);">
            <i class="fa-solid fa-hourglass-half text-warning" style="font-size:2.2rem;"></i>
          </span>
        </div>
        <h3 class="mb-1">Application Submitted</h3>
        <span class="badge bg-warning text-dark px-3 py-2 fs-6 mb-3">Pending Review</span>
        <p class="text-muted mb-4">
          Your application has been received and is currently under review.<br>
          We'll send an email to <strong>{{ form.personalInfo.email }}</strong> with updates.
        </p>
      </div>

      <div class="card border mb-4">
        <div class="card-body">
          <h6 class="f-w-600 mb-3"><i class="fa-solid fa-list-check me-2 text-primary"></i>What happens next?</h6>
          <ol class="mb-0 ps-3 text-muted small">
            <li class="mb-2">Our team will verify your license and insurance documents (2–3 business days).</li>
            <li class="mb-2">A background check will be initiated once your documents are approved.</li>
            <li class="mb-2">You'll need to complete your Stripe payout setup and upload vehicle &amp; profile photos.</li>
            <li>Once everything clears, you'll receive an approval email and can start accepting rides.</li>
          </ol>
        </div>
      </div>

      <div class="text-center">
        <a :href="submittedState.redirectUrl" class="btn btn-primary px-4">
          <i class="fa-solid fa-arrow-up-right-from-square me-2"></i>View Full Application Status
        </a>
      </div>
    </div>

    <div id="independent-driver-signup-wizard" v-show="isJurisdictionValidated && !submittedState">
      <div class="card-body p-3 p-md-4">
        <div class="horizontal-wizard-wrapper">
          <div class="row">
            <div class="col-12 main-horizontal-header mb-3 border-1">
              <div class="nav nav-pills horizontal-options" role="tablist" aria-orientation="horizontal">
                <a class="nav-link" data-bs-toggle="pill" href="#signup-step-personal" role="tab" aria-selected="true">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-id-card"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Personal Info</h5>
                    </div>
                  </div>
                </a>

                <a class="nav-link" data-bs-toggle="pill" href="#signup-step-verification" role="tab"
                   aria-selected="false">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-shield-check"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Verification</h5>
                    </div>
                  </div>
                </a>

                <a class="nav-link" data-bs-toggle="pill" href="#signup-step-vehicle" role="tab" aria-selected="false">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-car"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Vehicle</h5>
                    </div>
                  </div>
                </a>

                <a class="nav-link" data-bs-toggle="pill" href="#signup-step-documents" role="tab"
                   aria-selected="false">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-file-upload"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Documents</h5>
                    </div>
                  </div>
                </a>

                <a class="nav-link" data-bs-toggle="pill" href="#signup-step-review" role="tab" aria-selected="false">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-clipboard-check"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Review</h5>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <div class="col-12 tab-content dark-field p-2 p-sm-3 p-md-4">
              <div class="tab-pane fade active show wizard-content" id="signup-step-personal" role="tabpanel">
                <independent-driver-signup-step-personal-info v-model="form.personalInfo"/>
              </div>

              <div class="tab-pane fade wizard-content" id="signup-step-verification" role="tabpanel">
                <independent-driver-signup-step-verification v-model="form.verification"/>
              </div>

              <div class="tab-pane fade wizard-content" id="signup-step-vehicle" role="tabpanel">
                <driver-onboarding-step-vehicle-info v-model="form.vehicleInfo"/>
              </div>

              <div class="tab-pane fade wizard-content" id="signup-step-documents" role="tabpanel">
                <driver-onboarding-step-document-upload ref="documentsStep" v-model="form.documents"/>
              </div>

              <div class="tab-pane fade wizard-content" id="signup-step-review" role="tabpanel">
                <independent-driver-signup-step-review v-model="form.review" @editStep="goToStep"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer" v-show="!submittedState">
        <div id="independent-driver-signup-navigation"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, onUnmounted, ref, nextTick, provide, watch} from 'vue';
import 'jquery-ui';
import './../../extras/js/jquery-wizard';

import IndependentDriverSignupPersonalInfoModel from '../modelTemplates/IndependentDriverSignupPersonalInfo';
import IndependentDriverSignupVerificationModel from '../modelTemplates/IndependentDriverSignupVerification';
import DriverOnboardingVehicleInfoModel from '../modelTemplates/DriverOnboardingVehicleInfo';
import DriverOnboardingDocumentUploadModel from '../modelTemplates/DriverOnboardingDocumentUpload';
import DriverOnboardingReviewSubmitModel from '../modelTemplates/DriverOnboardingReviewSubmit';

import Swal from "../../extras/js/sweetalert/sweetalert2.min";
import {notificationType, showToast} from '../utility/toastNotificationUtility';
import DriverOnboardingStepDocumentUpload from "./DriverOnboardingStepDocumentUpload.vue";
import DriverOnboardingStepVehicleInfo from "./DriverOnboardingStepVehicleInfo.vue";
import IndependentDriverSignupStepPersonalInfo from "./IndependentDriverSignupStepPersonalInfo.vue";
import IndependentDriverSignupStepVerification from "./IndependentDriverSignupStepVerification.vue";
import IndependentDriverSignupStepReview from "./IndependentDriverSignupStepReview.vue";
import JurisdictionCheckModal from "./JurisdictionCheckModal.vue";

const props = defineProps({
  googleMapsApiKey: {
    type: String,
    required: true
  }
});

const form = ref({
  personalInfo: {},
  verification: {},
  vehicleInfo: {},
  documents: {},
  review: {},
});

const personalModel = IndependentDriverSignupPersonalInfoModel();
const verificationModel = IndependentDriverSignupVerificationModel();
const vehicleModel = DriverOnboardingVehicleInfoModel();
const documentsModel = DriverOnboardingDocumentUploadModel();
const reviewModel = DriverOnboardingReviewSubmitModel();

form.value.personalInfo = personalModel.state;
form.value.verification = verificationModel.state;
form.value.vehicleInfo = vehicleModel.state;
form.value.documents = documentsModel.state;
form.value.review = reviewModel.state;

// Wizard step positions. The validator, the per-step failure message, the completion
// checks and the review step's Edit links all read from here: the last reorder moved
// Verification from 3 to 1 and left three call sites pointing at the old numbers.
const STEP = {
  PERSONAL: 0,
  VERIFICATION: 1,
  VEHICLE: 2,
  DOCUMENTS: 3,
  REVIEW: 4,
};

provide('stepIndex', STEP);
provide('personalInfo', personalModel.state);
provide('vehicleInfo', vehicleModel.state);
provide('documentsInfo', documentsModel.state);
provide('reviewModel', reviewModel);

const documentsStep = ref(null);
const currentStepIndex = ref(0);
const isSaving = ref(false);
const isJurisdictionValidated = ref(false);
const submittedState = ref(null); // set after successful submission: { redirectUrl }

const emailModified = ref(false);
const phoneModified = ref(false);

provide('emailModified', emailModified);
provide('phoneModified', phoneModified);


watch(() => form.value.personalInfo.email, (newVal, oldVal) => {
  if (oldVal && newVal !== oldVal) {
    //if email verified we should notify that re-verification is necesssary
    if (form.value.verification.emailVerified) {
      emailModified.value = true;
      removeCompletionCheckOnTab(STEP.VERIFICATION);
    }

    form.value.verification.emailVerified = false;
  }
});

watch(() => `${form.value.personalInfo.countryCode}${form.value.personalInfo.phoneNumber}`, (newVal, oldVal) => {

  if (oldVal && newVal !== oldVal) {
    // if phone verified we should notify that re-verification is necesssary
    if (form.value.verification.phoneVerified) {
      phoneModified.value = true;
      removeCompletionCheckOnTab(STEP.VERIFICATION);
    }

    form.value.verification.phoneVerified = false;
  }
});


function onJurisdictionValidated() {
  isJurisdictionValidated.value = true;
  showBeforeYouBeginModal();
}

function showBeforeYouBeginModal() {
  Swal.fire({
    title: 'Before You Begin',
    html: `
      <div class="text-start">
        <p>Please have the following documents ready to upload:</p>
        <ul class="list-unstyled">
          <li><i class="fa-solid fa-id-card text-primary me-2"></i> Driver's License (front and back)</li>
          <li><i class="fa-solid fa-shield text-primary me-2"></i> Insurance Certificate (Pink Slip)</li>
        </ul>
        <p class="text-muted mb-0"><small>You'll also need the expiry dates for each document.</small></p>
      </div>
    `,
    icon: 'info',
    confirmButtonText: 'I\'m Ready',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });
}

function goToStep(stepIndex) {
  try {
    $('#independent-driver-signup-wizard').wizard('goTo', stepIndex);
  } catch (err) {
    console.error('Failed to navigate to step', stepIndex, err);
  }
}

// On a phone the step strip scrolls sideways rather than wrapping into ragged rows, so the step
// the driver was just moved to can be off screen. Nothing else scrolls it back.
function revealStepInStrip(idx) {
  const el = $('#independent-driver-signup-wizard .nav.nav-pills.horizontal-options > a').eq(idx)[0];
  el?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
}

function removeCompletionCheckOnTab(idx) {
  const $a = $('#independent-driver-signup-wizard .nav.nav-pills.horizontal-options > a').eq(idx);
  if (!$a || !$a.length) return;
  const $title = $a.find('.horizontal-wizard-content h5');
  if (!$title || !$title.length) return;
  $title.find('.step-complete-check').remove();
}

function addCompletionCheck(idx) {
  const $a = $('#independent-driver-signup-wizard .nav.nav-pills.horizontal-options > a').eq(idx);
  if (!$a || !$a.length) return;
  const $title = $a.find('.horizontal-wizard-content h5');
  if (!$title || !$title.length) return;
  if ($title.find('.step-complete-check').length) return;
  $title.append('<i class="fa-solid fa-check text-success ms-2 step-complete-check" title="Completed" aria-hidden="true"></i>');
}

const INCOMPLETE_STEP_MESSAGE = 'Please complete the required fields for this step.';

// Names the insurance declarations when they are the only thing outstanding: the checkboxes
// sit below the uploads, so a generic message sends the driver back to documents that are
// already correct.
function documentsStepMessage() {
  if (documentsModel.isValid()) return '';
  documentsStep.value?.revealDeclarationError();
  return documentsModel.hasAllDocuments()
      ? 'You must confirm all insurance eligibility declarations to continue.'
      : INCOMPLETE_STEP_MESSAGE;
}

// Each entry returns the message to show when its step is incomplete, or '' when it is valid,
// revealing any inline errors on the step itself as it goes.
// Keyed by STEP so a reorder cannot separate a step from its own message again.
const STEP_MESSAGES = {
  [STEP.PERSONAL]: () => (personalModel.isValid() ? '' : INCOMPLETE_STEP_MESSAGE),
  [STEP.VERIFICATION]: () => (verificationModel.isValid()
      ? ''
      : 'Please verify both your email address and phone number before continuing.'),
  [STEP.VEHICLE]: () => (vehicleModel.isValid() ? '' : INCOMPLETE_STEP_MESSAGE),
  [STEP.DOCUMENTS]: documentsStepMessage,
  [STEP.REVIEW]: () => (reviewModel.isValid() ? '' : INCOMPLETE_STEP_MESSAGE),
};

/**
 * Parses a failure body that may be either a JSON object or the plain text older endpoints return.
 *
 * @param {string} body the raw response body
 * @returns {object|null} the parsed object, or null when the body was not JSON
 */
function parseFailure(body) {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * Clears the lapsed verifications and returns the driver to the verification step.
 *
 * An OTP-verified marker expires server-side while the driver is still filling in later steps, so
 * the wizard can be showing "Verified" for something the server no longer accepts. Leaving those
 * badges up strands the driver on the review step with a rejection they have no way to clear.
 *
 * @param {{expired?: string[], error?: string}} failure the parsed verification_expired response
 */
function handleExpiredVerification(failure) {
  const expired = Array.isArray(failure.expired) && failure.expired.length > 0
      ? failure.expired
      : ['email', 'phone'];

  if (expired.includes('email')) verificationModel.state.emailVerified = false;
  if (expired.includes('phone')) verificationModel.state.phoneVerified = false;

  // The tick claims a step the driver has to redo; the wizard would otherwise let them walk
  // straight back to review and be refused a second time.
  removeCompletionCheckOnTab(STEP.VERIFICATION);
  goToStep(STEP.VERIFICATION);

  showToast({
    title: 'Verification Expired',
    body: failure.error || 'Your verification expired. Please request a new code to finish your application.',
    type: notificationType.WARN,
    delay: 10000
  });
}

async function submitSignup() {
  isSaving.value = true;

  const { referralCode, ...personalInfo } = personalModel.toJSON();

  const payload = {
    personalInfo,
    vehicleInfo: vehicleModel.toJSON(),
    documentsInfo: documentsModel.toJSON(),
    legalAgreements: reviewModel.toJSON(),
    referralCode: referralCode ? referralCode.trim() : null,
  };

  try {
    const fd = new FormData();
    fd.append('data', JSON.stringify(payload));

    const docsState = documentsModel.state;
    if (docsState?.driverLicenseFront) {
      fd.append('driverLicenseFile[0]', docsState.driverLicenseFront);
    }
    if (docsState?.driverLicenseBack) {
      fd.append('driverLicenseFile[1]', docsState.driverLicenseBack);
    }
    if (docsState?.insuranceDocFile) {
      fd.append('insuranceDocFile', docsState.insuranceDocFile);
    }

    const res = await fetch('/api/independent-driver-signup/complete', {
      method: 'POST',
      body: fd,
    });

    if (res.ok) {
      const data = await res.json();
      submittedState.value = { redirectUrl: data.redirectUrl || '/login' };
      showToast({
        title: 'Registration Complete',
        body: 'Your application has been submitted successfully! You will receive an email with next steps.',
        type: notificationType.SUCCESS
      });
      return true;
    }

    const errorText = await res.text();
    const failure = parseFailure(errorText);

    if (failure?.reason === 'verification_expired') {
      handleExpiredVerification(failure);
      return false;
    }

    showToast({
      title: 'Registration Failed',
      body: failure?.error || errorText || 'We could not complete your registration. Please try again.',
      type: notificationType.ERROR,
      delay: 8000
    });
    return false;
  } catch (e) {
    showToast({
      title: 'Network Error',
      body: 'We could not complete your registration. Please try again.',
      type: notificationType.ERROR,
    });
    return false;
  } finally {
    isSaving.value = false;
  }
}

function setupWizard() {
  $('#independent-driver-signup-wizard').wizard({
    step: '.nav-pills > a',
    classes: {
      step: {active: 'active'},
      pane: {active: 'active show'},
      button: {hide: 'd-none', disabled: 'd-none'},
    },
    buttonsAppendTo: '#independent-driver-signup-navigation',
    templates: {
      buttons: function () {
        return `<div class="pull-left">
                  <a role="button" class="btn btn-outline-secondary mb-3" href="#independent-driver-signup-wizard" data-wizard="back">Back</a>
                </div>
                <div class="pull-right">
                  <a role="button" class="btn btn-outline-primary mb-3" href="#independent-driver-signup-wizard" data-wizard="next">Continue</a>
                  <a role="button" class="btn btn-outline-success mb-3" href="#independent-driver-signup-wizard" data-wizard="finish">Submit Registration</a>
                </div>`;
      }
    },
    validator: function (step) {
      currentStepIndex.value = step.index;

      const messageFor = STEP_MESSAGES[step.index];
      if (!messageFor) return true;

      const message = messageFor();
      if (message) {
        showToast({
          title: 'Validation Failed',
          body: message,
          type: notificationType.WARN
        });
        return false;
      }
      return true;
    },
    onAfterChange: function (currentStep, nextStep) {
      if (nextStep.index > currentStep.index) {
        addCompletionCheck(currentStep.index);
        currentStepIndex.value = nextStep.index;
      }
      revealStepInStrip(nextStep.index);
    },
    onFinish: function () {
      submitSignup();
    }
  });
}

onMounted(async () => {
  const refParam = new URLSearchParams(window.location.search).get('ref');
  if (refParam) personalModel.state.referralCode = refParam;
  await nextTick();
  setupWizard();
});

onUnmounted(() => {
});
</script>

<style scoped>
.saving-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/* Brand accents (colors only) */
.ar-signup-header {
  border-top: 4px solid;
  border-image: linear-gradient(135deg, #4f3594 0%, #0983c0 100%) 1;
}

.ar-signup-header h2 {
  color: #231f20;
}

.ar-signup-link {
  color: #0983c0;
  text-decoration: none;
}

.ar-signup-link:hover {
  text-decoration: underline;
}

/* The wizard buttons are injected by jquery-wizard into the footer using a `pull-left` class the
   theme never defines, so on a narrow screen Back and Continue stacked in opposite directions. */
#independent-driver-signup-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}

:deep(#independent-driver-signup-navigation .pull-right) {
  float: none;
  display: flex;
  gap: 0.5rem;
}

@media (max-width: 575.98px) {
  /* Five steps wrapping onto three rows of different lengths reads as a broken layout rather than
     a sequence. One scrolling row keeps the order legible; revealStepInStrip keeps the current
     step in view. */
  :deep(.main-horizontal-header .nav-pills.horizontal-options) {
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 6px;
    scrollbar-width: none;
  }

  :deep(.main-horizontal-header .nav-pills.horizontal-options::-webkit-scrollbar) {
    display: none;
  }

  :deep(.main-horizontal-header .nav-pills.horizontal-options a.nav-link) {
    flex: 0 0 auto;
    padding: 0.45rem 0.6rem;
  }

  :deep(.main-horizontal-header .nav-pills.horizontal-options .horizontal-wizard) {
    gap: 6px;
  }

  :deep(.main-horizontal-header .nav-pills.horizontal-options .horizontal-wizard-content h5) {
    font-size: 13px;
    margin-bottom: 0;
    white-space: nowrap;
  }

  :deep(.main-horizontal-header .nav-pills.horizontal-options .stroke-icon-wizard i) {
    font-size: 14px;
  }
}

:deep(.btn-primary) {
  background: linear-gradient(135deg, #4f3594 0%, #0983c0 100%);
  border: 0;
}

:deep(.btn-primary:hover:not(:disabled)) {
  filter: brightness(1.07);
}
</style>
